import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import { IOption } from '@cmps/ng-select';
import { finalize, tap } from 'rxjs/operators';

import { Util } from '../../common/common.util';
import { PopUpService } from '../../insight/pop-up.service';
import { SearchUserContainerComponent } from '../../user/search-user.container.component';
import { SearchUserDialog } from '../../user/search-user.dialog';
import { TranslationKeyMap } from '../reports.i18n';
import { ReportsService } from '../reports.service';
import {
    TdBuilderUsageIndividualReportPopulationType,
    TdBuilderUsageIndividualReportSelectionParams,
} from '../td-builder-usage-individual/td-builder-usage-individual.reports.model';
import { isValidDateStruct } from '../td-daily-huddle/td-daily-huddle.reports.component';
import { TdRawAgentDataDates } from '../td-daily-huddle/td-daily-huddle.reports.model';

@Component({
    selector: '[cmpsTdBuilderUsageIndividualSelectionReport]',
    templateUrl:
        './td-builder-usage-individual-selection.reports.component.html',
    host: {
        class: 'cmps-reports-td-builder-usage-individual-selection',
    },
})
export class TdBuilderUsageIndividualSelectionComponent implements OnDestroy {
    @Input() params: TdBuilderUsageIndividualReportSelectionParams;
    @Input() roles: IOption[] = [];
    @Input() peerGroups: IOption[] = [];
    @Input() superGroups: IOption[] = [];
    @Input() reportPopulations: IOption[] = [];
    @Output() generated = new EventEmitter<number>();
    i18n = TranslationKeyMap;
    callComplianceDates: TdRawAgentDataDates;
    TdReportPopulationType = TdBuilderUsageIndividualReportPopulationType;
    currentDate = new Date();
    constructor(
        public popUpService: PopUpService,
        private readonly _reportService: ReportsService,
        private readonly _userSearchDialog: SearchUserDialog,
    ) {}

    get popUpVisible(): boolean {
        return (
            this.popUpService.active &&
            this.popUpService.active.componentRef.instance instanceof
                SearchUserContainerComponent
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get maxStartDate() {
        if (!this.params.endDate) {
            return;
        }
        if (Util.dateToDate(this.params.endDate) > this.currentDate) {
            return Util.dateToNgbDate(this.currentDate);
        }
        return { ...this.params.endDate, day: this.params.endDate.day - 1 };
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get todayDate() {
        return Util.dateToNgbDate(new Date());
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get minEndDate() {
        if (!this.params.startDate) {
            return;
        }
        return { ...this.params.startDate, day: this.params.startDate.day + 1 };
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get futureDate() {
        return Util.dateToDate(this.params.startDate) > this.currentDate;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get wrongDate() {
        return (
            Util.dateToDate(this.params.startDate) >
            Util.dateToDate(this.params.endDate)
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get generateReportDisabled() {
        return (
            !this.params.populationType ||
            !this.params.startDate ||
            !this.params.endDate ||
            !this.params.populationID ||
            this.wrongDate ||
            this.futureDate
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.popUpVisible) {
            this.popUpService.hide();
        }
        this._userSearchDialog.closeAll();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectDate() {
        this.params.noData = false;
        this.params.invalidStartDate = !isValidDateStruct(
            this.params.startDate,
        );
        this.params.invalidEndDate = !isValidDateStruct(this.params.endDate);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectPopulationType() {
        this.params.noData = false;
        if (
            [
                TdBuilderUsageIndividualReportPopulationType.Individual,
                TdBuilderUsageIndividualReportPopulationType.ReptsUnder,
            ].includes(this.params.populationType)
        ) {
            this.params.populationID = this.params.defaultPopulationID;
        } else {
            this.params.populationID = null;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectUser(e: MouseEvent) {
        this.params.noData = false;
        const target = e.target as HTMLElement;
        this._userSearchDialog.openUsingPopUp({
            options: {
                target,
                placement: 'bottom-right',
                containerClass: 'cmps-cdk-z-index-2',
            },
            close: (user) => {
                if (user) {
                    this.params.populationID = {
                        userId: user.id,
                        userName: `${user.firstName} ${user.lastName}`,
                    };
                    console.log(this.params.populationID);
                }
            },
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectPopulationID() {
        this.params.noData = false;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onGenerateReport() {
        if (this.generateReportDisabled) {
            return;
        }
        this.params.pendingData = true;
        this.params.noData = false;
        this._reportService
            .generateTdBuilderUsageIndividual(
                typeof this.params.populationID === 'string'
                    ? parseInt(this.params.populationID)
                    : this.params.populationID.userId,
                this.params.populationType,
                Util.dateToDate(this.params.startDate),
                Util.dateToDate(this.params.endDate),
            )
            .pipe(
                tap((r) => {
                    if (Number.isInteger(r) && r !== 0) {
                        this.generated.next(r);
                    } else {
                        this.params.noData = true;
                    }
                    // eslint-disable-next-line no-return-assign
                }),
                finalize(() => (this.params.pendingData = false)),
            )
            .subscribe();
        // this.generated.next(7);
    }
}
